import cx from 'classnames';
import { useIntl } from 'react-intl';
import CloseIcon from '@evaneos/b2b-ui/icons/Close';

import { Replay } from '../../../../../design-system/Icons/Replay';
import { CircularLoader } from '../../../../../design-system/Loader/CircularLoader';
import { AttachmentWithStatus } from '../../../../../resources/discussion/types';

import s from './NewMessageBox.css';

type Props = {
    attachment: AttachmentWithStatus;
    onDelete: () => void;
    retryUploadAttachment: (attachment: AttachmentWithStatus) => void;
};

export function Attachment({ attachment, onDelete, retryUploadAttachment }: Props): JSX.Element {
    const { formatMessage } = useIntl();
    const { file, category, status: attachmentStatus } = attachment;

    const hasCategory = !!category;
    const hasError = attachmentStatus === 'error';
    const isLoading = attachmentStatus === 'pending';
    const isLoaded = attachmentStatus === 'success';
    const type = file.name.replace(/.*\./, '');

    return (
        <div
            key={file.name}
            className={cx(s.attachment, { [s.attachmentError]: hasError })}
            data-testid="nmb-attachment"
        >
            <div className={s.type}>{type}</div>
            <p>{file.name}</p>
            {hasCategory && (
                <p className={s.category} data-testid="nmb-attachment-category">
                    {formatMessage({
                        id: convertCategoryToTranslation(category),
                    })}
                </p>
            )}
            {hasError && (
                <a
                    className={cx(s.attachmentIcon, s.retryAttachment)}
                    onClick={() => retryUploadAttachment(attachment)}
                    data-testid="nmb-retry"
                >
                    <Replay color="var(--red)" />
                </a>
            )}
            {isLoading && (
                <CircularLoader className={s.loader} data-testid="nmb-attachment-loader" />
            )}
            {isLoaded && (
                <a
                    className={cx(s.attachmentIcon)}
                    onClick={onDelete}
                    data-testid="nmb-delete-attachment"
                >
                    <CloseIcon sx={{ color: 'icon.primary', width: '24px', height: '24px' }} />
                </a>
            )}
        </div>
    );
}

function convertCategoryToTranslation(category: string | null) {
    switch (category) {
        case 'before_trip_quotation':
            return 'discussion.add-attachments-modal.categories.quote';
        case 'during_trip_roadbook':
            return 'discussion.add-attachments-modal.categories.roadbook';
        case 'before_trip_other':
        case 'during_trip_other':
        default:
            return 'discussion.add-attachments-modal.categories.other';
    }
}
